import { useState } from "react";
import { taskService } from "@miplatform/api";
import { ATTEMPT_STATUSES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  IApiRequest,
  IAttemptListItemResponseForWorkOrder,
  ICalendarWorkTemplateBase,
} from "@miplatform/types";
import { buildSearchQuery } from "@miplatform/utils";
import { useGlobalManagerApproval } from "@mibuildweb/components";

export const useAutoHoldListViewModel = ({
  calendarId,
  workOrderTemplates,
  search,
  filter,
  onRefresh,
}: {
  calendarId: string;
  workOrderTemplates: ICalendarWorkTemplateBase[];
  search?: string;
  filter?: Record<string, any>;
  onRefresh: () => void;
}) => {
  const stateManager = useComponentState();
  const [refresh, setRefresh] = useState(1);
  const [attempts, setAttempts] = useState<
    IAttemptListItemResponseForWorkOrder[]
  >([]);
  const { executeApiRequest } = useGlobalManagerApproval();

  const getAttempts = ({ top = 25, skip }: IApiRequest = {}) =>
    stateManager.run(async () => {
      const searchQuery = Object.entries(
        buildSearchQuery(search, [
          "home/address/addressLine1",
          "home/communityName",
          "home/builderName",
          "home/address/city",
        ])
      ).map(([key, value]) => ({
        [key]: value,
      }));
      return taskService.attempts.getAllWorkOrders({
        top,
        skip,
        filters: {
          ...filter,
          and: [
            ...((filter?.and as Record<string, any>[]) || []),
            ...searchQuery,
          ],
          attemptStatus: { eq: ATTEMPT_STATUSES.HOLD },
          assignment: {
            workOrder: {
              holdCalendarId: {
                type: "guid",
                value: calendarId,
              },
              workOrderTemplateId: {
                in:
                  workOrderTemplates?.map(({ id }) => ({
                    type: "guid",
                    value: id,
                  })) || [],
              },
            },
          },
        },
        sort: "modifiedOn desc",
      });
    });

  const releaseWorkOrder = (
    attempt: IAttemptListItemResponseForWorkOrder,
    reason: string
  ) => {
    return stateManager.run(async () => {
      const options = await taskService.workOrders.getReleaseOptions({
        id: attempt.assignment?.workOrder?.workOrderId,
        data: { reason },
        modifiedOn: attempt.assignment?.workOrder?.modifiedOn,
      });

      const response = await executeApiRequest({
        options,
        noInitialErrorMessage: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => prev + 1);
        onRefresh();
        toastService.success({
          text: "Work Order Released",
          subtext: `${attempt.home?.address.addressLine1} was released from hold`,
        });
      }

      return response.status === HTTP_STATUSES.OK;
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    attempts,
    refresh,
    getAttempts,
    releaseWorkOrder,
    setAttempts,
    setRefresh,
  };
};
